import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api.ts';

interface Setting {
    key: string;
    description: string | null;
    isSecret: boolean;
    isBoolean: boolean;
    choices: { value: string; label: string }[] | null;
    hasValue: boolean;
    value: string | null;
}

/** Libellés lisibles. La clé technique reste affichée en petit dessous : c'est
 *  elle qu'on retrouve dans les logs et dans les migrations. */
const LABELS: Record<string, string> = {
    google_client_id: 'Client ID Google',
    google_client_secret: 'Client secret Google',
    google_login_enabled: 'Connexion Google',
    password_login_enabled: 'Connexion par mot de passe',
    registration_mode: 'Nouvelle adresse Google inconnue',
    smtp_host: 'Serveur SMTP',
    smtp_port: 'Port SMTP',
    smtp_user: 'Compte SMTP',
    smtp_password: 'Mot de passe SMTP',
    smtp_from: 'Adresse d\'expédition',
    default_distance_unit: 'Unité de distance par défaut',
};

/** Regroupement d'affichage, par préfixe de clé. */
const GROUPS: { title: string; match: (k: string) => boolean }[] = [
    { title: 'Connexion Google', match: (k) => k.startsWith('google_') },
    { title: 'Comptes', match: (k) => k === 'registration_mode' || k === 'password_login_enabled' },
    { title: 'Messagerie', match: (k) => k.startsWith('smtp_') },
    { title: 'Divers', match: () => true },
];

export function AdminSettings() {
    const [settings, setSettings] = useState<Setting[]>([]);
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        void api<{ settings: Setting[] }>('/admin/settings')
            .then(({ settings: s }) => {
                setSettings(s);
                setDraft(Object.fromEntries(s.map((x) => [x.key, x.value ?? ''])));
            })
            .catch((e: Error) => setMessage({ kind: 'error', text: e.message }));
    }, []);

    const save = async (): Promise<void> => {
        setBusy(true);
        setMessage(null);
        try {
            const { changed } = await api<{ changed: string[] }>('/admin/settings', {
                method: 'PUT',
                body: Object.fromEntries(
                    Object.entries(draft).map(([k, v]) => [k, v === '' ? null : v]),
                ),
            });
            setMessage({ kind: 'ok', text: `${changed.length} réglage(s) enregistré(s).` });
        } catch (e) {
            setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Enregistrement impossible' });
        } finally {
            setBusy(false);
        }
    };

    const assigned = new Set<string>();
    const groups = GROUPS.map((g) => {
        const items = settings.filter((s) => !assigned.has(s.key) && g.match(s.key));
        items.forEach((s) => assigned.add(s.key));
        return { title: g.title, items };
    }).filter((g) => g.items.length > 0);

    return (
        <div>
            <p className="mb-4 text-sm opacity-60">
                Stockés en base, donc conservés à chaque déploiement — contrairement au fichier
                d'environnement, régénéré par le pipeline.
            </p>

            {message && (
                <div
                    role="alert"
                    className={`alert mt-4 text-sm ${message.kind === 'ok' ? 'alert-success' : 'alert-error'}`}
                >
                    <span>{message.text}</span>
                </div>
            )}

            <div className="mt-6 space-y-6">
                {groups.map((group) => (
                    <section key={group.title}>
                        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider opacity-50">
                            {group.title}
                        </h2>
                        <div className="space-y-4 rounded-box bg-base-200 p-4">
                            {group.items.map((s) => (
                                s.isBoolean ? (
                                    /* Interrupteur, pas un champ où saisir
                                       « true » : un réglage à deux états se
                                       bascule, il ne s'orthographie pas. */
                                    <label key={s.key} className="flex cursor-pointer items-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-primary mt-0.5 shrink-0"
                                            checked={draft[s.key] === 'true'}
                                            onChange={(e) => setDraft((d) => ({
                                                ...d, [s.key]: e.target.checked ? 'true' : 'false',
                                            }))}
                                        />
                                        <span className="min-w-0">
                                            <span className="block text-sm font-medium">
                                                {LABELS[s.key] ?? s.key}
                                            </span>
                                            {s.description && (
                                                <span className="mt-0.5 block text-xs opacity-60">
                                                    {s.description}
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                ) : s.choices ? (
                                    /* Trois états ne se basculent pas : une
                                       liste dit ce que chaque valeur signifie,
                                       là où « true » n'explique rien. */
                                    <label key={s.key} className="block w-full">
                                        <span className="mb-1 block text-sm font-medium">
                                            {LABELS[s.key] ?? s.key}
                                        </span>
                                        <select
                                            className="select select-bordered w-full"
                                            value={draft[s.key] ?? s.choices[0]!.value}
                                            onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
                                        >
                                            {s.choices.map((c) => (
                                                <option key={c.value} value={c.value}>{c.label}</option>
                                            ))}
                                        </select>
                                        {s.description && (
                                            <span className="mt-1 block text-xs opacity-60">{s.description}</span>
                                        )}
                                    </label>
                                ) : (
                                    <label key={s.key} className="block w-full">
                                        <span className="mb-1 block text-sm font-medium">
                                            {LABELS[s.key] ?? s.key}
                                            <span className="ml-2 font-mono text-[10px] font-normal opacity-40">
                                                {s.key}
                                            </span>
                                        </span>
                                        <input
                                            type={s.isSecret ? 'password' : 'text'}
                                            className="input input-bordered input-sm w-full"
                                            value={draft[s.key] ?? ''}
                                            placeholder={s.isSecret && s.hasValue ? 'valeur enregistrée' : ''}
                                            onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
                                        />
                                        {s.description && (
                                            <span className="mt-1 block text-xs opacity-60">{s.description}</span>
                                        )}
                                    </label>
                                )
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            <button
                type="button"
                className="btn btn-primary mt-6 w-full tap-target sm:w-auto"
                onClick={() => void save()}
                disabled={busy}
            >
                {busy ? <span className="loading loading-spinner" /> : <Save size={20} />}
                Enregistrer
            </button>

            <p className="mt-3 text-xs opacity-50">
                Un secret déjà enregistré s'affiche comme un masque et n'est jamais renvoyé en
                clair. Le laisser tel quel le conserve ; le remplacer l'écrase.
            </p>
        </div>
    );
}
